import { CertificateExtendedClient } from '@frontend/certificate/api';
import { EquipmentCertificateExtended } from '@frontend/certificate/types';
import { ClassType, EntityType } from '@frontend/common';
import { StatusBadge, accountIdFromResolvedEntity } from '@frontend/components/common';
import { GroupedListGroup, GroupedObjectList, defaultCellClassName } from '@frontend/components/list';
import { DocumentClient } from '@frontend/document/api';
import { EquipmentExtended, IEquipment } from '@frontend/equipment/types';
import { Application } from '@frontend/functionality/types';
import { ProductCategoryClient } from '@frontend/group/api';
import { IdentificationClient } from '@frontend/identification/api';
import { CommonMessage } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import { IMAGE_MIME_TYPES, useBlobRepository } from '@frontend/repository';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { CategoriesContainer } from '../../applications/modals/containers/categories-container';
import { CommonContainer } from '../../applications/modals/containers/common-container';
import { ConstraintsContainer } from '../../applications/modals/containers/constraints-container';
import { ImageContainerWrapper } from '../../applications/modals/containers/image-wrapper/image-container-wrapper.components';
import { PlatformDetailGrid } from '../../modal/platform-detail-grid/platform-detail-grid.component';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';
import { useAppDispatch } from '../../redux/store';

export const Expired = (
    <FormattedMessage
        id='certificate.status.expired'
        defaultMessage='Expired'
    />
);
export const Certified = (
    <FormattedMessage
        id='certificate.status.certified'
        defaultMessage='Certified'
    />
);

export const EquipmentDetailsCard = ({ equipment }: { equipment: EquipmentExtended }) => (
    <div className='inline-flex w-full items-start justify-start gap-4 self-stretch'>
        <div className='bg-background-surface outline-border-default inline-flex flex-1 flex-col items-start justify-start gap-6 rounded-md px-4 pt-4 pb-6 outline-1 outline-offset-[-1px]'>
            <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
            <PlatformDetailGrid
                object={equipment}
                cols={2}
                order={['serial_number', 'state', 'product', 'tag', 'user', 'owner']}
                keyOverwrite={
                    new Map([
                        ['serial_number', CommonMessage.OBJECTS.EQUIPMENT.SN],
                        ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS],
                        ['user', CommonMessage.OBJECTS.USER.DEFAULT]
                    ])
                }
                valueOverwrite={
                    new Map<string, (obj: EquipmentExtended) => React.ReactNode>([
                        [
                            'product',
                            (obj) =>
                                obj.product ? (
                                    <PlatformObjectBadge
                                        display={obj.product.name}
                                        entityType={EntityType.PRODUCT}
                                        id={obj.product.entity_id}
                                        foreign={obj.product.foreign}
                                        accountId={accountIdFromResolvedEntity(obj.product)}
                                    />
                                ) : (
                                    <span>/</span>
                                )
                        ],
                        [
                            'user',
                            (obj) =>
                                obj.user ? (
                                    <PlatformObjectBadge
                                        display={obj.user.name}
                                        entityType={EntityType.USER}
                                        id={obj.user.entity_id}
                                        foreign={obj.user.foreign}
                                        accountId={accountIdFromResolvedEntity(obj.user)}
                                    />
                                ) : (
                                    <span>/</span>
                                )
                        ],
                        [
                            'owner',
                            (obj) =>
                                obj.owner ? (
                                    <PlatformObjectBadge
                                        display={obj.owner.name}
                                        entityType={EntityType.USER}
                                        id={obj.owner.entity_id}
                                        foreign={obj.owner.foreign}
                                        accountId={accountIdFromResolvedEntity(obj.owner)}
                                    />
                                ) : (
                                    <span>/</span>
                                )
                        ],
                        [
                            'tag',
                            (obj) =>
                                obj.tag ? (
                                    <PlatformObjectBadge
                                        display={obj.tag.name}
                                        entityType={EntityType.TAG}
                                        id={obj.tag.entity_id}
                                        foreign={obj.tag.foreign}
                                        accountId={accountIdFromResolvedEntity(obj.tag)}
                                    />
                                ) : (
                                    <span>/</span>
                                )
                        ]
                    ])
                }
            />
        </div>
        <ImageContainerWrapper
            entity={equipment}
            entity_type={EntityType.EQUIPMENT}
        />
    </div>
);

export const EquipmentGeneralSection = ({ equipment }: { equipment: EquipmentExtended }) => (
    <div className='inline-flex w-full items-start justify-start gap-4 self-stretch'>
        <CommonContainer
            containerTitle={CommonMessage.OBJECTS.ATTRIBUTE.PLURAL}
            keyHeader={CommonMessage.OBJECTS.ATTRIBUTE.ATTRIBUTE_NAME}
            valueHeader={CommonMessage.OBJECTS.ATTRIBUTE_VALUE.VALUE}
            data={equipment.product?.data?.attributes ?? null}
        />
        <CategoriesContainer
            entity_id={equipment.id}
            entity_type={EntityType.EQUIPMENT}
            categories={equipment.categories}
        />
    </div>
);

export const EquipmentCertificatesSection = ({ equipment }: { equipment: EquipmentExtended }) => {
    const dispatch = useAppDispatch();
    return (
        <GroupedObjectList<EquipmentCertificateExtended, { categoryName: string }>
            label={CommonMessage.OBJECTS.CERTIFICATE.PLURAL}
            customLabelClassName='inline-flex items-start justify-start gap-2.5 self-stretch px-4 pt-4 pb-4'
            groupedListProps={{
                groupBy: (cert) => {
                    const groupMap = new Map<string, EquipmentCertificateExtended[]>();
                    cert.forEach((certificate) => {
                        if (!certificate.certificate || !certificate.certificate.name) return;
                        if (!groupMap.has(certificate.certificate.name)) {
                            groupMap.set(certificate.certificate.name, []);
                        }
                        groupMap.get(certificate.certificate.name)!.push(certificate);
                    });
                    const result: GroupedListGroup<EquipmentCertificateExtended, { categoryName: string }>[] = [];
                    groupMap.forEach((items, name) => {
                        result.push({
                            id: name,
                            group: { categoryName: name },
                            items,
                            defaultExpanded: false
                        });
                    });
                    return result;
                },
                renderGroupHeader: (group) => group.categoryName
            }}
            fetch={async (arg) =>
                CertificateExtendedClient.fetchEquipmentCertificates({
                    ...arg,
                    account_id: equipment.account_id,
                    equipment_id: equipment.id,
                    sort_field: 'certificate_id',
                    sort_direction: 'asc'
                })
            }
            dispatch={dispatch}
            columns={{
                columns: ['certificate', 'state', 'from_timestamp', 'to_timestamp'],
                cellClassName: 'text-foreground-default align-center items-center truncate text-sm font-medium',
                headerOverwrite: new Map([
                    ['certificate', CommonMessage.OBJECTS.COMMON_FIELDS.NAME],
                    ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS],
                    ['creation_timestamp', CommonMessage.OBJECTS.BUDGET.FROM_TIMESTAMP],
                    ['update_timestamp', CommonMessage.OBJECTS.BUDGET.TO_TIMESTAMP]
                ]),
                customCells: new Map([
                    [
                        'certificate',
                        (obj) =>
                            obj.row.original.certificate ? (
                                <td className={defaultCellClassName}>
                                    <PlatformObjectBadge
                                        entityType={EntityType.CERTIFICATE}
                                        id={obj.row.original.certificate.entity_id}
                                        display={obj.row.original.certificate.name}
                                        foreign={obj.row.original.certificate.foreign}
                                        accountId={accountIdFromResolvedEntity(obj.row.original.certificate)}
                                    />
                                </td>
                            ) : (
                                <td></td>
                            )
                    ],
                    [
                        'state',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                <StatusBadge
                                    value={obj.row.original.expired ? Expired : Certified}
                                    colorMap={
                                        new Map([
                                            [[Certified], ClassType.SUCCESS],
                                            [[Expired], ClassType.WARNING]
                                        ])
                                    }
                                />
                            </td>
                        )
                    ],
                    [
                        'certificate_id',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                <PlatformObjectBadge
                                    entityType={EntityType.CERTIFICATE}
                                    id={obj.row.original.certificate_id}
                                />
                            </td>
                        )
                    ]
                ])
            }}
            showCount={false}
            type={EntityType.CERTIFICATE}
        />
    );
};

export const EquipmentConstraintsSection = ({ equipment }: { equipment: EquipmentExtended }) => {
    const dispatch = useAppDispatch();
    const { openModal } = useModals();
    return (
        <ConstraintsContainer
            currentAccount={equipment.account_id}
            dispatch={dispatch}
            entityType='equipment'
            entityId={equipment.id}
            entityName={equipment.serial_number}
            openModal={openModal}
        />
    );
};

/** Laundry equipment uses a simpler detail card (base IEquipment + product image/categories). */
export const LaundryEquipmentDetailsCard = ({ equipment }: { equipment: IEquipment }) => {
    const blobRepository = useBlobRepository();
    const [imageUrl, setImageUrl] = useState<string | null | undefined>(undefined);
    const [categories, setCategories] = useState<string[] | undefined>(undefined);
    const [tag, setTag] = useState<string | undefined>(undefined);

    useEffect(() => {
        ProductCategoryClient.fetchProductCategoryMembers({ entity_id: equipment.product_id }).then((res) => {
            setCategories(res.results.map((member) => member.group_id));
        });
    }, [equipment.product_id]);

    useEffect(() => {
        IdentificationClient.fetchIdentifications({
            account_id: equipment.account_id,
            entity_type: EntityType.EQUIPMENT,
            entity_id: equipment.id
        }).then((response) => {
            setTag(response.results[0]?.key);
        });
    }, [equipment.account_id, equipment.id]);

    useEffect(() => {
        let cancelled = false;
        setImageUrl(undefined);

        const loadImage = async () => {
            const blobInfo = await blobRepository.getBlob(equipment.account_id, EntityType.PRODUCT, equipment.product_id);
            if (cancelled) return;
            if (blobInfo && IMAGE_MIME_TYPES.includes(blobInfo.mimeType)) {
                setImageUrl(blobInfo.url);
                return;
            }

            const documents = await DocumentClient.fetchDocuments({ entity_id: equipment.product_id, entity_type: EntityType.PRODUCT });
            if (cancelled) return;

            const imageDocument = documents.results.find(
                (document) => document.document_info?.mime_type && IMAGE_MIME_TYPES.includes(document.document_info.mime_type)
            );
            if (!imageDocument) {
                setImageUrl(null);
                return;
            }

            const sasUrl = await DocumentClient.generateDocumentSasUrl(imageDocument.account_id, imageDocument.id);
            if (!cancelled) setImageUrl(sasUrl);
        };

        void loadImage().catch(() => {
            if (!cancelled) setImageUrl(null);
        });

        return () => {
            cancelled = true;
        };
    }, [equipment, blobRepository]);

    return (
        <div className='flex w-full gap-3'>
            <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
                <PlatformDetailGrid
                    object={equipment}
                    cols={2}
                    order={['serial_number', 'tag', 'product_id', 'category', 'owner_id', 'user_id']}
                    valueOverwrite={
                        new Map([
                            ['tag', () => <span>{tag}</span>],
                            [
                                'category',
                                () => (
                                    <div className='flex flex-col gap-2'>
                                        {categories
                                            ? categories.map((category) => (
                                                  <PlatformObjectBadge
                                                      entityType={EntityType.PRODUCT_CATEGORY}
                                                      id={category}
                                                      key={category}
                                                  />
                                              ))
                                            : null}
                                    </div>
                                )
                            ]
                        ])
                    }
                    entityTypeOverwrite={
                        new Map([
                            ['product_id', EntityType.PRODUCT],
                            ['owner_id', EntityType.USER],
                            ['user_id', EntityType.USER]
                        ])
                    }
                />
            </div>
            <div className='bg-background-surface outline-border-default inline-flex w-64 items-center justify-center gap-2.5 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                {imageUrl === undefined ? (
                    <span className='text-border-default' />
                ) : imageUrl === null ? (
                    <p>{CommonMessage.ERRORS.NOT_FOUND}</p>
                ) : (
                    <img
                        className='aspect-square rounded-md object-cover'
                        src={imageUrl}
                        alt='Laundry Equipment'
                    />
                )}
            </div>
        </div>
    );
};

export function isLaundryEquipmentContext(context: Record<string, string>): boolean {
    return context.application === Application.LAUNDRY;
}

export const EQUIPMENT_CERTIFICATES_LABEL = (
    <FormattedMessage
        id='platform.equipment.modal.equipment.reactive-detail.certificates-tab'
        description='Tab label for certificates on the equipment detail pane'
        defaultMessage='Certificates'
    />
);
